import { useCallback } from 'react';

import { useGraphQLClient } from '../hooks';

import {
  GET_CANONICAL_PRODUCT,
  GET_PACKAGE_BY_CODE,
  GET_PRODUCT_BY_CODE,
  GET_PRODUCT_SET_BY_CODE
} from '../../graphql/product-detail-queries';

import { getVehicleInput } from '../../utils/fitment/fitment';
import { getTreadwellGQLInput } from '../../utils/treadwell/treadwell';

import { PRODUCT_COMPARE_SELECTOR_TYPE } from '../../constants/products';

function useProductDetail({ fitmentPosition, myStore, nearByStoreCodes }) {
  const client = useGraphQLClient();

  const getProductDetail = useCallback(
    async ({
      canonicalFilters,
      canonicalProductCodes,
      canonicalProductType,
      fitment,
      packageCode,
      productCode,
      productSlug,
      rearProductCode,
      searchTypes,
      shouldFetchAllCanonicalData,
      store,
      treadwellPreferences
    }) => {
      const isStaggered = Boolean(rearProductCode);
      const isPrebuiltPackage = Boolean(packageCode);
      const isCanonicalSearch = Boolean(!productCode && !packageCode);

      function getCanonicalQueryVariables() {
        const variables = {
          id: productSlug,
          nearByStoreCodes,
          options: {
            filters: canonicalFilters,
            productCodes: canonicalProductCodes || []
          },
          productType: canonicalProductType,
          storeCode: store?.code,
          vehicleInfo: !shouldFetchAllCanonicalData
            ? getVehicleInput(fitment)
            : null
        };

        return variables;
      }
      function getQueryVariables() {
        const DEFAULT_WET_DISTANCE_ZIP_CODE = '67205';
        let treadwellRequest = null;
        let vehicleInfo = null;
        let zipCode = null;

        const storeCode = store?.code;
        const isStaggered = Boolean(rearProductCode);
        const isPrebuiltPackage = Boolean(packageCode);

        const productSelectorType = searchTypes?.find(
          product => product.code === productCode
        )?.compareSelectorType;

        if (fitment) {
          vehicleInfo = getVehicleInput(fitment) || null;
          treadwellRequest =
            productSelectorType === PRODUCT_COMPARE_SELECTOR_TYPE.ALL_TIRES
              ? null
              : getTreadwellGQLInput(fitment, treadwellPreferences);
        }

        if (!window.isPreRendering) {
          zipCode =
            treadwellRequest?.zipCode ||
            myStore?.address?.postalCode ||
            DEFAULT_WET_DISTANCE_ZIP_CODE;
        }

        const variables = {
          nearByStoreCodes,
          storeCode,
          treadwellRequest,
          vehicleInfo,
          weatherZipCode: zipCode
        };

        if (isStaggered) {
          variables['frontCode'] = productCode;
          variables['rearCode'] = rearProductCode;
        } else if (isPrebuiltPackage) {
          variables['packageCode'] = packageCode;
        } else {
          variables['productCode'] = productCode;
          variables['fitmentPosition'] = fitmentPosition;
        }
        return variables;
      }

      const queryVariables = isCanonicalSearch
        ? getCanonicalQueryVariables()
        : getQueryVariables();

      let query = GET_PRODUCT_BY_CODE;
      let queryType = 'byCode';
      if (isStaggered) {
        query = GET_PRODUCT_SET_BY_CODE;
        queryType = 'setByCode';
      } else if (isPrebuiltPackage) {
        query = GET_PACKAGE_BY_CODE;
        queryType = 'packageByCode';
      } else if (isCanonicalSearch) {
        query = GET_CANONICAL_PRODUCT;
        queryType = 'canonicalById';
      }

      const { data, errors } = await client.query({
        fetchPolicy: isCanonicalSearch ? 'network-only' : 'cache-first',
        query,
        variables: queryVariables
      });

      return {
        errors: errors,
        product: data?.product?.[queryType],
        queryType,
        recommendations: data?.recommendationQueries
      };
    },
    [client, fitmentPosition, myStore, nearByStoreCodes]
  );

  return {
    getProductDetail
  };
}

export default useProductDetail;
